import { useCallback, useState } from "react";
import { useCropTypeById } from "../../hooks/useCropTypes";
import { cropTypesApi, type CropType } from "../../services/cropTypesApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { PagedCombobox } from "@/components/ui/pagedcombobox";
import { Plus, ChevronDown, ChevronUp } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { getFieldErrors, getErrorMessage } from "@/shared/utils/errorHandler";
import { useSubmitSetupApproval } from "../../hooks/useSetupApprovals";
import { SETUP_APPROVAL_TYPE } from "../../services/setupApprovalApi";
import { useConfirm } from "@/shared/contexts/confirm-context";

const initialFormData = {
  name: "",
  code: "",
  description: "",
  notes: "",
  // Lifecycle
  sowingToGerminationDays: "",
  sowingToSeedlingDays: "",
  sowingToTransplantReadyDays: "",
  sowingToOvergrownDays: "",
  acclimationDays: "",
  plantingToMaturityDays: "",
  harvestingPeriodDays: "",
  stockPercent: "",
};

type FormData = typeof initialFormData;

const parseDay = (val: string) => (val === "" ? undefined : parseInt(val, 10));
const parseDecimal = (val: string) => (val === "" ? undefined : parseFloat(val));

function validateLifecycle(
  data: FormData
): Record<string, string[]> | null {
  const errors: Record<string, string[]> = {};

  const germ = parseDay(data.sowingToGerminationDays);
  const seedling = parseDay(data.sowingToSeedlingDays);
  const transplant = parseDay(data.sowingToTransplantReadyDays);
  const overgrown = parseDay(data.sowingToOvergrownDays);

  const allDayFields: [keyof FormData, number | undefined][] = [
    ["sowingToGerminationDays", germ],
    ["sowingToSeedlingDays", seedling],
    ["sowingToTransplantReadyDays", transplant],
    ["sowingToOvergrownDays", overgrown],
    ["acclimationDays", parseDay(data.acclimationDays)],
    ["plantingToMaturityDays", parseDay(data.plantingToMaturityDays)],
    ["harvestingPeriodDays", parseDay(data.harvestingPeriodDays)],
  ];

  for (const [field, val] of allDayFields) {
    if (val !== undefined && val < 1) {
      errors[field] = ["Must be at least 1 day"];
    }
  }

  const stockPercent = parseDecimal(data.stockPercent);
  if (stockPercent !== undefined && (stockPercent < 0 || stockPercent > 100)) {
    errors.stockPercent = ["Must be between 0 and 100"];
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export const CreateCropVarietyDialog = ({ defaultCropTypeId }: { defaultCropTypeId?: string } = {}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [selectedCropType, setSelectedCropType] = useState<CropType | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [showLifecycle, setShowLifecycle] = useState(false);

  const submitMutation = useSubmitSetupApproval();
  const notify = useNotification();
  const confirm = useConfirm();
  const defaultCropTypeQuery = useCropTypeById(defaultCropTypeId ?? "");

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const applyLifecycleDefaults = useCallback((cropType: CropType | null) => {
    setFormData((prev) => ({
      ...prev,
      sowingToGerminationDays: String(cropType?.sowingToGerminationDays ?? ""),
      sowingToSeedlingDays: String(cropType?.sowingToSeedlingDays ?? ""),
      sowingToTransplantReadyDays: String(cropType?.sowingToTransplantReadyDays ?? ""),
      sowingToOvergrownDays: String(cropType?.sowingToOvergrownDays ?? ""),
      acclimationDays: String(cropType?.acclimationDays ?? ""),
      plantingToMaturityDays: String(cropType?.plantingToMaturityDays ?? ""),
      harvestingPeriodDays: String(cropType?.harvestingPeriodDays ?? ""),
    }));
  }, []);

  const handleCropTypeChange = (cropType: CropType | null) => {
    setSelectedCropType(cropType);
    applyLifecycleDefaults(cropType);
    if (cropType) setShowLifecycle(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && defaultCropTypeQuery.data && !selectedCropType) {
      handleCropTypeChange(defaultCropTypeQuery.data);
    }
    setOpen(nextOpen);
  };

  const fetchCropTypes = useCallback(
    ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) =>
      cropTypesApi.getCropTypesPaged({ search, pageNumber, pageSize, isActive: true, sortBy: "name" }),
    [],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    // Validate required lifecycle fields
    const requiredLifecycleFields = [
      "sowingToGerminationDays",
      "sowingToSeedlingDays",
      "sowingToTransplantReadyDays",
      "sowingToOvergrownDays",
    ];
    const missingRequired = requiredLifecycleFields.filter(
      (field) => formData[field as keyof FormData] === ""
    );
    if (missingRequired.length > 0) {
      const newErrors: Record<string, string[]> = {};
      missingRequired.forEach((field) => {
        newErrors[field] = ["This field is required"];
      });
      setFieldErrors(newErrors);
      setShowLifecycle(true);
      return;
    }

    const lifecycleErrors = validateLifecycle(formData);
    if (lifecycleErrors) {
      setFieldErrors(lifecycleErrors);
      setShowLifecycle(true);
      return;
    }

    if (!selectedCropType) {
      notify.error("Select a crop type");
      return;
    }

    const payload = {
      cropTypeId: selectedCropType.id,
      name: formData.name,
      code: formData.code || undefined,
      description: formData.description || undefined,
      notes: formData.notes || undefined,
      sowingToGerminationDays: parseDay(formData.sowingToGerminationDays),
      sowingToSeedlingDays: parseDay(formData.sowingToSeedlingDays),
      sowingToTransplantReadyDays: parseDay(
        formData.sowingToTransplantReadyDays
      ),
      sowingToOvergrownDays: parseDay(formData.sowingToOvergrownDays),
      acclimationDays: parseDay(formData.acclimationDays),
      plantingToMaturityDays: parseDay(formData.plantingToMaturityDays),
      harvestingPeriodDays: parseDay(formData.harvestingPeriodDays),
      stockPercent: parseDecimal(formData.stockPercent),
    };

    confirm({
      title: "Submit variety for approval?",
      description: `“${payload.name}” (${selectedCropType.name}) will be submitted for approval before it becomes available.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitMutation
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.CropVariety,
            entityJson: JSON.stringify(payload),
            description: `New Crop Variety: ${payload.name}`,
          })
          .then(() => {
            notify.success("Submitted for approval");
            setFormData(initialFormData);
            setSelectedCropType(null);
            setOpen(false);
          })
          .catch((error: unknown) => {
            const fe = getFieldErrors(error);
            if (fe) {
              setFieldErrors(fe);
            } else {
              notify.error(getErrorMessage(error));
            }
            throw error;
          }),
    });
  };

  const lifecycleFields: { key: keyof FormData; label: string }[] = [
    { key: "sowingToGerminationDays", label: "Days to Germination" },
    { key: "sowingToSeedlingDays", label: "Days to Seedling" },
    { key: "sowingToTransplantReadyDays", label: "Days to Transplant-Ready" },
    { key: "sowingToOvergrownDays", label: "Days to Overgrown" },
    { key: "acclimationDays", label: "Acclimation Days" },
    { key: "plantingToMaturityDays", label: "Days to Maturity (field)" },
    { key: "harvestingPeriodDays", label: "Harvest Window (days)" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Variety
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Crop Variety</DialogTitle>
          <DialogDescription>
            Add a new variety for a crop type
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field>
            <FieldLabel htmlFor="cropTypeId">
              Crop Type <span className="text-red-500">*</span>
            </FieldLabel>
            <PagedCombobox<CropType>
              title="Crop Type"
              value={selectedCropType}
              onChange={handleCropTypeChange}
              fetchPage={fetchCropTypes}
              disabled={submitMutation.isPending || !!defaultCropTypeId}
            />
            {fieldErrors.cropTypeId && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.cropTypeId.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="name">
              Variety Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="name"
              placeholder="e.g., Beefsteak, Roma"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              disabled={submitMutation.isPending}
            />
            {fieldErrors.name && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.name.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="code">Code</FieldLabel>
            <Input
              id="code"
              placeholder="Optional variety code"
              value={formData.code}
              onChange={(e) => handleChange("code", e.target.value)}
              disabled={submitMutation.isPending}
            />
            {fieldErrors.code && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.code.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <Textarea
              id="description"
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={submitMutation.isPending}
            />
            {fieldErrors.description && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.description.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <Textarea
              id="notes"
              placeholder="Optional notes"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              disabled={submitMutation.isPending}
            />
            {fieldErrors.notes && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.notes.join(", ")}
              </div>
            )}
          </Field>

          {/* Lifecycle Defaults — collapsible */}
          <div className="border rounded-md">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
              onClick={() => setShowLifecycle((v) => !v)}
            >
              Lifecycle Defaults
              {showLifecycle ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {showLifecycle && (
              <div className="px-4 pb-4 space-y-3 border-t">
                <p className="text-xs text-muted-foreground pt-3">
                  <span className="font-semibold">Required:</span> Germination, Seedling, Transplant-Ready, Overgrown.
                  <br />
                  <span className="font-semibold">Optional:</span> Acclimation, Maturity, Harvest Window.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                  {lifecycleFields.map(({ key, label }) => {
                    const isRequired = [
                      "sowingToGerminationDays",
                      "sowingToSeedlingDays",
                      "sowingToTransplantReadyDays",
                      "sowingToOvergrownDays",
                    ].includes(key);
                    return (
                      <Field key={key}>
                        <FieldLabel htmlFor={key}>
                          {label}
                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </FieldLabel>
                        <Input
                          id={key}
                          type="number"
                          min={1}
                          placeholder="days"
                          value={formData[key]}
                          onChange={(e) => handleChange(key, e.target.value)}
                          disabled={submitMutation.isPending}
                          required={isRequired}
                        />
                        {fieldErrors[key] && (
                          <div className="text-red-500 text-xs mt-1">
                            {fieldErrors[key].join(", ")}
                          </div>
                        )}
                      </Field>
                    );
                  })}
                </div>

                <Field>
                  <FieldLabel htmlFor="stockPercent">Stock %</FieldLabel>
                  <Input
                    id="stockPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    placeholder={
                      selectedCropType?.stockPercent != null
                        ? String(selectedCropType.stockPercent)
                        : "70"
                    }
                    value={formData.stockPercent}
                    onChange={(e) => handleChange("stockPercent", e.target.value)}
                    disabled={submitMutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Override the crop type default. Filling Reserve:{" "}
                    {(() => {
                      const v = parseFloat(formData.stockPercent);
                      if (Number.isFinite(v)) return (100 - v).toFixed(1);
                      const inherited = selectedCropType?.stockPercent ?? 70;
                      return (100 - inherited).toFixed(1);
                    })()}
                    %. Leave blank to inherit.
                  </p>
                  {fieldErrors.stockPercent && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.stockPercent.join(", ")}
                    </div>
                  )}
                </Field>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitMutation.isPending || !selectedCropType || !formData.name}
          >
            {submitMutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
